import React, {
    ReactNode,
    useRef,
    useState,
    useEffect,
    CSSProperties,
    forwardRef
} from "react"
import { Panel, Button } from "dash-styled"
import ResizeObserver from "resize-observer-polyfill"

interface ButtonWithPanelProps {
    isOpen: boolean
    buttonText: string
    panelBody: ReactNode
    testId?: string
    color?: "plain" | "outline-pink"
    /**
     * class names which are applied when the panel is closed
     */
    classNameClosed?: string
    /**
     * class names which are applied when the panel is open
     */
    classNameOpen?: string
    disabled?: boolean
    onClickButton: () => void
}

export const ButtonWithPanel = forwardRef<HTMLDivElement, ButtonWithPanelProps>(
    (
        {
            isOpen: isPanelShown,
            buttonText,
            panelBody,
            testId,
            classNameClosed,
            classNameOpen,
            color = "plain",
            disabled,
            onClickButton
        }: ButtonWithPanelProps,
        ref
    ) => {
        const [isMaxHeight, setMaxHeight] = useState(false)
        const panelRef = useRef<HTMLDivElement>(null)
        const buttonRef = useRef<HTMLButtonElement>(null)
        const [panelStyle, setPanelStyle] = useState<CSSProperties>({
            visibility: "hidden"
        })

        useEffect(() => {
            if (!isPanelShown) {
                return
            }

            function updateLayout() {
                if (panelRef.current === null || buttonRef.current === null) {
                    return
                }

                const buttonRect = buttonRef.current.getBoundingClientRect()
                const panelWidth = panelRef.current.offsetWidth
                const panelRange = window.innerWidth - panelWidth
                const buttonRange = window.innerWidth - buttonRect.width
                const maxHeight =
                    window.innerHeight - panelRef.current.offsetTop - 10

                setPanelStyle({
                    left: `${Math.ceil(
                        buttonRect.left * (panelRange / buttonRange)
                    )}px`,
                    top: `${Math.ceil(buttonRect.top + buttonRect.height)}px`,
                    maxHeight: `${maxHeight}px`
                })
                setMaxHeight(maxHeight === panelRef.current.offsetHeight)
            }

            window.addEventListener("resize", updateLayout)

            const resizeObserver = new ResizeObserver(updateLayout)
            const mutationObserver =
                "MutationObserver" in window
                    ? new MutationObserver(updateLayout)
                    : undefined

            updateLayout()

            if (panelRef.current !== null) {
                resizeObserver.observe(panelRef.current)
                mutationObserver?.observe(window.document, {
                    characterData: true,
                    attributes: false,
                    childList: true,
                    subtree: true
                })
            }

            return () => {
                window.removeEventListener("resize", updateLayout)
                resizeObserver.disconnect()
                mutationObserver?.disconnect()
            }
        }, [panelRef, buttonRef, setPanelStyle, isPanelShown, setMaxHeight])

        return (
            <div ref={ref} className="flex items-stretch">
                <Button
                    onClick={onClickButton}
                    size="xs"
                    color={color}
                    variant="contained2"
                    ref={buttonRef}
                    testId={testId}
                    disabled={disabled}
                    className={
                        isPanelShown
                            ? classNameOpen || ""
                            : classNameClosed || ""
                    }
                >
                    {buttonText}
                </Button>
                {isPanelShown ? (
                    <Panel
                        className={`rounded fixed z-10 -mt-px mr-2 border border-solid border-gray-400 text-sm ${
                            isMaxHeight ? "overflow-auto" : "overflow-visible"
                        }`}
                        style={panelStyle}
                        ref={panelRef}
                    >
                        {panelBody}
                    </Panel>
                ) : null}
            </div>
        )
    }
)
